import { setAppConfig } from '../../lib/app-config.js';
import { authState, fetchConfig } from '../../lib/auth.js';
import { BaseComponent } from '../../lib/base-component.js';
import { css, html } from '../../lib/lit.js';
import { router } from '../../lib/router.js';
import './app-nav.js';
import '../views/analytics-view.js';
import '../views/beta-queue-redirect-view.js';
import '../views/changelog-editor-view.js';
import '../views/changelog-view.js';
import '../views/et-item-view.js';
import '../views/et-plan-view.js';
import '../views/et-plans-view.js';
import '../views/invitations-view.js';
import '../views/list-contact-view.js';
import '../views/lists-view.js';
import '../views/not-authorized-view.js';
import '../views/not-found-view.js';
import '../views/poll-editor-view.js';
import '../views/poll-results-view.js';
import '../views/polls-view.js';
import '../views/sign-in-view.js';

// The app shell. This console is entirely staff-only — every route requires a
// d3-auth session. (The public invitee/legal surfaces this app used to also
// serve now live in d3-auth, which owns invitations and consent.) _start()
// fetches /config, then initializes auth; an unauthenticated visitor lands on
// the sign-in view.
class StaffApp extends BaseComponent {
    static properties = {
        _route: { state: true },
        _params: { state: true },
        _meta: { state: true },
        _authReady: { state: true },
        _signInUnavailable: { state: true },
        _configError: { state: true },
    };

    static styles = css`
        :host {
            display: block;
            min-height: 100%;
        }
    `;

    constructor() {
        super();
        this._route = null;
        this._params = {};
        this._meta = {};
        this._authReady = false;
        this._signInUnavailable = false;
        this._configError = '';

        this._unsubscribeRouter = router.subscribe((state) => {
            this._route = state.route;
            this._params = state.params;
            this._meta = state.meta;
        });
        this._unsubscribeAuth = authState.subscribe(() => this.requestUpdate());
    }

    connectedCallback() {
        super.connectedCallback();
        this._start();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._unsubscribeRouter?.();
        this._unsubscribeAuth?.();
    }

    async _start() {
        router.handleRoute();

        try {
            const config = await fetchConfig();
            setAppConfig(config);
            this._signInUnavailable = !config.d3AuthClientID || !config.d3AuthTenantSlug;
            if (!this._signInUnavailable) {
                await authState.init(config);
            }
        } catch (error) {
            this._configError = error.message;
        }

        this._authReady = true;

        // Re-resolve: completing an OAuth callback rewrites the URL back to
        // wherever the user started.
        router.handleRoute();
    }

    render() {
        if (this._configError) {
            return html`<div class="alert alert-danger m-4" role="alert">
                Could not load configuration: ${this._configError}
            </div>`;
        }

        if (!this._authReady) {
            return html`<p class="text-muted m-4">Loading…</p>`;
        }

        // Checked BEFORE authState.loading, which matters: when sign-in is
        // unconfigured we never call authState.init(), and `loading` initializes
        // to true and is only cleared there — so testing it first left the app
        // spinning on "Loading…" forever in exactly the state this branch exists
        // to report. Fail loudly, not quietly.
        if (this._signInUnavailable) {
            return html`<d3-sign-in-view unavailable></d3-sign-in-view>`;
        }

        if (authState.loading) {
            return html`<p class="text-muted m-4">Loading…</p>`;
        }

        if (!authState.isAuthenticated) {
            return html`<d3-sign-in-view></d3-sign-in-view>`;
        }

        // Authenticated but not a staff member: a real, reachable state (signing
        // in with a non-staff account). Show a clear screen with a way out rather
        // than dropping them into the app to hit a 403 on the first gated call.
        if (!authState.isStaff) {
            return html`<d3-not-authorized-view
                email=${authState.user?.email ?? ''}
            ></d3-not-authorized-view>`;
        }

        return html`
            <d3-app-nav
                email=${authState.user?.email ?? ''}
                route=${this._route ?? ''}
            ></d3-app-nav>
            ${this._renderStaff()}
        `;
    }

    _renderStaff() {
        switch (this._route) {
            case 'invitations':
                return html`<d3-invitations-view></d3-invitations-view>`;
            case 'lists':
                return html`<d3-lists-view></d3-lists-view>`;
            case 'list-contact':
                return html`<d3-list-contact-view contact-id=${this._params.id}></d3-list-contact-view>`;
            case 'beta-queue-entry':
                return html`<d3-beta-queue-redirect-view
                    entry-id=${this._params.id}
                ></d3-beta-queue-redirect-view>`;
            case 'changelog':
                return html`<d3-changelog-view></d3-changelog-view>`;
            case 'changelog-new':
                return html`<d3-changelog-editor-view></d3-changelog-editor-view>`;
            case 'changelog-edit':
                return html`<d3-changelog-editor-view
                    post-id=${this._params.id}
                ></d3-changelog-editor-view>`;
            case 'polls':
                return html`<d3-polls-view></d3-polls-view>`;
            case 'poll-new':
                return html`<d3-poll-editor-view></d3-poll-editor-view>`;
            case 'poll-edit':
                return html`<d3-poll-editor-view poll-id=${this._params.id}></d3-poll-editor-view>`;
            case 'poll-results':
                return html`<d3-poll-results-view poll-id=${this._params.id}></d3-poll-results-view>`;
            case 'analytics':
                return html`<d3-analytics-view></d3-analytics-view>`;
            case 'et-plans':
                return html`<d3-et-plans-view></d3-et-plans-view>`;
            case 'et-plan':
                return html`<d3-et-plan-view plan-id=${this._params.planId}></d3-et-plan-view>`;
            case 'et-item':
                return html`<d3-et-item-view item-id=${this._params.itemId}></d3-et-item-view>`;
            case 'auth-callback':
                // init() has already exchanged the code and replaced the URL, so
                // this is only ever a brief flash.
                return html`<p class="text-muted m-4">Signing you in…</p>`;
            default:
                return html`<d3-not-found-view></d3-not-found-view>`;
        }
    }
}

customElements.define('d3-staff-app', StaffApp);
